import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './UserModal.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const UserModal = ({ user, fixedRole, selectedClient, selectedManager: propSelectedManager, onClose, onSuccess }) => {
  const [selectedManager, setSelectedManager] = useState(propSelectedManager);
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: '',
    clientId: '',
    managerId: ''
  });
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSelectedManager(propSelectedManager);
  }, [propSelectedManager]);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        password: '',
        name: user.name || '',
        role: user.role || ''
      });
    } else {
      // Determine default role based on context
      if (fixedRole) {
        setFormData(prev => ({ ...prev, role: fixedRole }));
      } else if (selectedManager) {
        setFormData(prev => ({ ...prev, role: 'employee', managerId: selectedManager.id || selectedManager._id }));
      } else if (selectedClient) {
        setFormData(prev => ({ ...prev, role: currentUser?.role === 'superadmin' ? 'manager' : 'employee' }));
      } else if (currentUser?.role === 'superadmin') {
        setFormData(prev => ({ ...prev, role: 'client' }));
      } else if (currentUser?.role === 'client') {
        setFormData(prev => ({ ...prev, role: 'manager' }));
      } else if (currentUser?.role === 'manager') {
        setFormData(prev => ({ ...prev, role: 'employee' }));
      }
    }

    // Fetch clients for superadmin
    if (currentUser?.role === 'superadmin' && !user) {
      fetchClients();
    }
    
    // Fetch managers based on context
    if (!user) {
      if (selectedClient) {
        // If selectedClient is provided (from context)
        const clientId = selectedClient.id || selectedClient._id || selectedClient;
        fetchManagers(clientId);
      } else if (currentUser?.role === 'client') {
        // If current user is client, fetch managers under their clientId
        fetchManagers(currentUser.id || currentUser._id);
      } else if (formData.clientId && currentUser?.role === 'superadmin') {
        // If superadmin selected a client, fetch managers for that client
        fetchManagers(formData.clientId);
      }
    }
  }, [user, fixedRole, selectedClient, selectedManager, currentUser, formData.clientId]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users`);
      const clientsList = response.data.filter(u => u.role === 'client');
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchManagers = async (clientId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/users`);
      // Normalize clientId for comparison
      const normalizedClientId = String(clientId);
      const managersList = response.data.filter(u => {
        const userClientId = u.clientId ? String(u.clientId) : null;
        return userClientId === normalizedClientId && u.role === 'manager';
      });
      setManagers(managersList);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        username: formData.username,
        name: formData.name,
        role: formData.role
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      // Handle user creation based on role and current user's role
      if (!user) {
        // Creating new user
        if (!formData.password) {
          setError('Password is required for new users');
          setLoading(false);
          return;
        }

        if (currentUser?.role === 'superadmin') {
          // Superadmin can create client, manager, or employee
          if (formData.role === 'client') {
            // No additional fields needed for client
          } else if (formData.role === 'manager') {
            // Manager needs clientId
            if (!formData.clientId && !selectedClient) {
              setError('Please select a client');
              setLoading(false);
              return;
            }
            payload.clientId = formData.clientId || (selectedClient ? (selectedClient.id || selectedClient._id || selectedClient) : null);
          } else if (formData.role === 'employee') {
            // Employee needs clientId (required) and optionally managerId
            if (!formData.clientId && !selectedClient) {
              setError('Please select a client');
              setLoading(false);
              return;
            }
            payload.clientId = formData.clientId || (selectedClient ? (selectedClient.id || selectedClient._id || selectedClient) : null);
            
            // ManagerId is optional for superadmin creating employee
            if (selectedManager) {
              payload.managerId = selectedManager.id || selectedManager._id;
            } else if (formData.managerId) {
              payload.managerId = formData.managerId;
            }
          }
        } else if (currentUser?.role === 'client') {
          // Client can create manager or employee
          if (formData.role === 'manager') {
            // Manager's clientId is auto-assigned from current user (handled by backend)
            // No need to send clientId
          } else if (formData.role === 'employee') {
            // Employee needs managerId
            // clientId is auto-assigned from current user (handled by backend)
            if (selectedManager) {
              payload.managerId = selectedManager.id || selectedManager._id;
            } else if (formData.managerId) {
              payload.managerId = formData.managerId;
            } else {
              setError('Please select a manager');
              setLoading(false);
              return;
            }
          }
        } else if (currentUser?.role === 'manager') {
          // Manager can only create employee
          // Both clientId and managerId are auto-assigned from current user (handled by backend)
          // No additional fields needed
        }
      } else {
        // Updating existing user
        // Only name and password can be updated
      }

      if (user) {
        await axios.put(`${BASE_URL}/api/users/${user.id || user._id}`, payload);
        if (onSuccess) {
          onSuccess('User updated successfully');
        }
      } else {
        await axios.post(`${BASE_URL}/api/users`, payload);
        if (onSuccess) {
          onSuccess('User created successfully');
        }
      }

      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Error saving user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {user
              ? 'Edit User'
              : fixedRole
                ? `Create ${fixedRole.charAt(0).toUpperCase()}${fixedRole.slice(1)}`
                : 'Create User'}
          </h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              disabled={!!user}
            />
          </div>

          <div className="form-group">
            <label>Password {user ? '(leave blank to keep current)' : '*'}</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
            />
          </div>

          {!fixedRole && (
            <div className="form-group">
              <label>Role *</label>
              <select
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  // Reset dependent fields when role changes
                  setFormData({ 
                    ...formData, 
                    role: newRole,
                    clientId: newRole === 'client' ? '' : formData.clientId,
                    managerId: newRole !== 'employee' ? '' : formData.managerId
                  });
                  // Fetch managers if role is employee and we have clientId
                  if (newRole === 'employee') {
                    if (currentUser?.role === 'client') {
                      fetchManagers(currentUser.id || currentUser._id);
                    } else if (formData.clientId) {
                      fetchManagers(formData.clientId);
                    } else if (selectedClient) {
                      const clientId = selectedClient.id || selectedClient._id || selectedClient;
                      fetchManagers(clientId);
                    }
                  } else {
                    setManagers([]);
                  }
                }}
                required
                disabled={!!user}
              >
                <option value="">Select Role</option>
                {currentUser?.role === 'superadmin' && (
                  <>
                    <option value="client">Client</option>
                    <option value="manager">Manager</option>
                    <option value="employee">Employee</option>
                  </>
                )}
                {currentUser?.role === 'client' && (
                  <>
                    <option value="manager">Manager</option>
                    <option value="employee">Employee</option>
                  </>
                )}
                {currentUser?.role === 'manager' && (
                  <option value="employee">Employee</option>
                )}
              </select>
            </div>
          )}

          {/* Client selection for superadmin creating manager or employee */}
          {currentUser?.role === 'superadmin' && (formData.role === 'manager' || formData.role === 'employee') && !selectedClient && !user && (
            <div className="form-group">
              <label>Client *</label>
              <select
                value={formData.clientId || ''}
                onChange={(e) => {
                  const newClientId = e.target.value;
                  setFormData({ ...formData, clientId: newClientId, managerId: '' }); // Reset managerId when client changes
                  if (newClientId) {
                    fetchManagers(newClientId);
                  } else {
                    setManagers([]);
                  }
                }}
                required
              >
                <option value="">Select Client</option>
                {clients.map(client => (
                  <option key={client.id || client._id} value={client.id || client._id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Manager selection for employee creation */}
          {formData.role === 'employee' && !user && currentUser?.role !== 'manager' && !selectedManager && (
            <div className="form-group">
              <label>Manager {currentUser?.role === 'client' ? '*' : ''}</label>
              <select
                value={formData.managerId || ''}
                onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
                required={currentUser?.role === 'client'}
                disabled={!formData.clientId && currentUser?.role === 'superadmin'}
              >
                <option value="">Select Manager</option>
                {managers.map(manager => (
                  <option key={manager.id || manager._id} value={manager.id || manager._id}>
                    {manager.name}
                  </option>
                ))}
              </select>
              {managers.length === 0 && (formData.clientId || selectedClient || currentUser?.role === 'client') && (
                <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  No managers available. Please create a manager first.
                </p>
              )}
              {currentUser?.role === 'superadmin' && !formData.clientId && (
                <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Please select a client first to see managers.
                </p>
              )}
            </div>
          )}

          {formData.role === 'employee' && !user && selectedManager && (
            <div className="form-group">
              <p style={{ color: '#27ae60', fontSize: '0.85rem', fontStyle: 'italic' }}>
                This employee will be assigned to manager: <strong>{selectedManager.name}</strong>
              </p>
            </div>
          )}

          {/* Info message for manager creating employee */}
          {formData.role === 'employee' && !user && currentUser?.role === 'manager' && (
            <div className="form-group">
              <p style={{ color: '#27ae60', fontSize: '0.85rem', fontStyle: 'italic' }}>
                This employee will be automatically assigned to you as their manager.
              </p>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : user ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;

